import * as React from 'react';
import { cloneDeep } from 'lodash';
import {
  Operation,
  GetOperationName,
  SourceType
} from '@config/index';
import {
  TriggerCondition,
  Attribute,
  ReferenceNode,
  Reference,
  ResponseConfig,
  ParameterConfig,
  FrameType
} from '@interface/index';
import { BodyGroup, ExprMode, HelperPopover, NiInput, NiRadio, NiTable, ReferenceTextArea } from '@component/index';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { TriggerTable } from './trigger-table';
import { localizedString } from '@locale/index';
import { ReferenceTextRender } from '@component/reference-textarea';
import { ParameterMapStore, isEqual } from '@lib/index';
import { AddButton } from '@component/buttons';
import CodeTextArea from '@component/code-textarea/code-textarea';

type ConditionRow = {
  variable: Reference;
  realIndex: number;
  contentIndex: number;
  rowSpan: number;
  operation: Operation;
  operationAt?: string;
  value: any;
};

export interface TriggerConditionListProps {
  response: ResponseConfig;
  intentId: string;
  data: TriggerCondition[];
  onConditionListChange: (next: TriggerCondition[]) => void;
  onResponseChange: (next: ResponseConfig) => void;
  options: ParameterConfig[];
  disabled?: boolean;
}

export interface TriggerConditionListState {
  data: TriggerCondition[];
  codeCondition: string;
  codeComment?: string;
  visible: boolean;
  cur?: number;
  new: boolean;
  targetSpm?: Attribute;
}

export class TriggerConditionList extends React.Component<
  TriggerConditionListProps,
  TriggerConditionListState
> {
  public state: TriggerConditionListState = {
    data: [],
    codeCondition: '',
    visible: false,
    new: false
  };

  onInit = () => {
    this.setState({
      data: this.props.data,
      codeCondition: this.props.response.codeCondition,
      codeComment: this.props.response.codeComment
    });
  };

  getIntentInfo = () => {
    const { intentId } = this.props;
    const intent = ParameterMapStore.get(intentId);
    return intent;
  }

  componentDidMount() {
    this.onInit();
  }

  componentDidUpdate(prevProps: any) {
    if (!isEqual(prevProps.response, this.props.response)) {
      this.onInit();
    }
  }

  render() {
    const props = this.props;
    const state = this.state;

    return (
      <BodyGroup
        style={{ marginTop: 16 }}
        title={localizedString('common_condition')}
        titleExtra={<HelperPopover localizedKey="response_condition" />}
        extra={props.intentId &&
          props.response.conditionType === SourceType.REGULAR &&
          <NiRadio
            disabled={props.disabled}
            style={{
              marginTop: 4
            }}
            value={props.response.conditionType}
            onChange={(e: any) => {
              const nextResp = cloneDeep(props.response);
              nextResp.conditionType = e.target.value;
              props.onResponseChange(nextResp);
            }}
            keyValue={[
              { key: SourceType.REGULAR, value: localizedString('condition_type_regular') },
              { key: SourceType.CODE_EXPRESSION, value: localizedString('condition_type_code_expression') }
            ]}
          /> || []}
      >{props.response.conditionType === SourceType.REGULAR
        ? <NiTable
          className="trigger-condition-table"
          dataSource={this.flattenData()}
          rowKey={(record: ConditionRow) =>
            `${record.realIndex}-${record.contentIndex}`
          }
          columns={this.conditionColumns()}
          onRow={(row: ConditionRow) => {

            return {
              onClick: () => {
                this.handleConditionClick(row.realIndex);
              }
            };
          }}
          pagination={false}
        />
        :
        <CodeTextArea
          dataId="branch_condition"
          disabled={props.disabled}
          data={state.codeCondition}
          update={this.handleCodeConditionChange}
          language="kotlin"
          completionContext={{
            rawCodeType: this.getIntentInfo(),
            rawCodeExprMode: ExprMode.condition
          }}
        />
          // <ReferenceTextArea
          //   disabled={props.disabled}
          //   parameterMap={ParameterMapStore.all()}
          //   initParameters={[]}
          //   data={state.codeCondition}
          //   rawCodeMode={true}
          //   rawCodeType={this.getIntentInfo()}
          //   rawCodeExprMode={ExprMode.condition}
          //   placeholder={localizedString('code_condition_placeholder')}
          //   update={this.handleCodeConditionChange}
          //   comment={state.codeComment}
          //   onCommentChange={(value: string) => {

          //     const props = this.props;
          //     if (value === props.response.codeCondition) {
          //       return;
          //     }

          //     const nextResp = cloneDeep(props.response);
          //     nextResp.codeComment = value;
          //     props.onResponseChange(nextResp);
          //   }}
          // // onBlur={this.handleCodeConditionChange}
          // // onPressEnter={this.handleCodeConditionChange}
          // />
        }
        {(
          <TriggerTable
            triggerConditions={props.data}
            response={props.response}
            disabled={props.disabled}
            key={`${state.cur}`}
            frameId={props.intentId}
            frameType={FrameType.intent}
            parameters={props.options}
            data={state.new ? undefined : state.data[state.cur || 0]}
            visible={state.visible}
            confirm={this.onConditionChange}
            close={this.onCancel}
          />
        )}
      </BodyGroup >
    );
  }

  flattenData = () => {
    const { data } = this.props;

    const ret: ConditionRow[] = [];
    for (const [i, variable] of data.entries()) {
      for (const [j, content] of variable.conditions.entries()) {
        ret.push({
          variable: variable.variable,
          realIndex: i,
          contentIndex: j,
          operation: content.operation,
          operationAt: content.operationAt,
          value: content.value,
          rowSpan: j === 0 ? variable.conditions.length : 0
        });
      }
    }

    return ret;
  }

  conditionColumns = () => {
    const { disabled } = this.props;
    return [
      {
        width: '33%',
        title: 'Conditions',
        dataIndex: 'variable',
        render: (item: Reference, record: ConditionRow, index: number) => {

          let variableName = (item || []).map((node: ReferenceNode) => {
            let name = node.name;
            if (node.showIndex) {
              name = `${name}[${node.index || 0}]`;
            }
            return name;
          }).join('.');

          const isTableSize = record.operationAt === 'table_row';

          if (isTableSize) {
            variableName = variableName + '.size';
          }

          const obj = {
            children: variableName,
            props: {
              rowSpan: record.rowSpan
            }
          };

          return obj;
        }
      },
      {
        width: '33%',
        title: 'Operator',
        key: 'operator',
        dataIndex: 'operation',
        render: (item: Operation, record: ConditionRow, index: number) => {
          return GetOperationName(item);
        }
      },
      {
        width: '33%',
        title: 'Value',
        dataIndex: 'value',
        render: (item: any, record: ConditionRow) => {
          if (record.operation === Operation.IsTypeCondition) {
            const param = ParameterMapStore.get(item[0]);
            return param && param.name || item;
          }

          return <ReferenceTextRender text={item.join(', ')} parameterMap={ParameterMapStore.all()} />;
        }
      },
      {
        title: <PlusOutlined
          onClick={this.handleCreateCondition}
        />,
        className: 'hide',
        key: 'operation',
        width: 50,
        render: (
          operation: any,
          record: ConditionRow,
          index: number
        ) => {
          return (
            <div className="icon-area">
              {!disabled &&
                <DeleteOutlined
                  className="delete-icon"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    this.handleDeleteConditionContent(record);
                  }}
                />
              }
            </div>
          );
        }
      }
    ];
  }

  handleConditionClick = async (
    index: number
  ) => {
    const { disabled = false } = this.props;
    // if (disabled) {
    //   message.warning(localizedString('common_editing_warning'));

    //   return;
    // }

    this.setState(
      {
        cur: index,
        visible: true,
        new: false
      }
    );
  };

  handleCreateCondition = () => {
    this.setState({
      new: true,
      visible: true
    });
  };

  handleDeleteSingleCondition = async (
    index: number,
    e?: any
  ) => {
    if (e) {
      e.stopPropagation();
    }

    const props = this.props;
    const nextData = cloneDeep(props.data);
    nextData.splice(index, 1);
    props.onConditionListChange(nextData);
  };

  handleDeleteConditionContent = (record: ConditionRow) => {

    const props = this.props;
    const nextData = cloneDeep(props.data);

    const variable = nextData[record.realIndex];

    if (variable.conditions && variable.conditions.length <= 1) {
      this.handleDeleteSingleCondition(record.realIndex);

      return;
    }

    variable.conditions.splice(record.contentIndex, 1);

    props.onConditionListChange(nextData);
  }

  onConditionChange = async (next: TriggerCondition) => {
    const state = this.state;

    const nextData = cloneDeep(state.data);

    if (state.new) {
      nextData.push(next);
    } else {
      nextData[state.cur || 0] = next;
    }

    this.closeDialog(() => {
      this.setState({
        data: nextData
      }, () => {
        this.props.onConditionListChange(this.state.data);
      });
    });
  };

  onCancel = () => {
    const state = this.state;

    if (state.new) {
      const nextData = state.data;
      // nextData.splice(state.cur || 0, 1);

      this.closeDialog(() => {
        this.setState({ data: nextData });
      });
    } else {
      this.setState({
        visible: false,
        cur: -1
      });
    }
  };


  handleCodeConditionChange = (value: any) => {
    const props = this.props;

    if (value === props.response.codeCondition) {
      return;
    }

    const nextResp = cloneDeep(props.response);
    nextResp.codeCondition = value;
    props.onResponseChange(nextResp);
  };

  closeDialog = (callback?: () => void) => {
    this.setState({
      visible: false,
      new: false,
      cur: -1
    }, callback);
  };
}
